import React from 'react';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import { Checkbox } from 'src/components/PulseUI/Checkbox';
import { Tags } from 'src/components/PulseUI/Tags';
import PushToCRMGroup from 'src/components/PushToCRM/PushToCRMGroup';
import { MedRMStatus } from 'src/components/MedRM/MedRMStatus';
import TypeIcon from 'src/components/TypeIcon';
import { formatSocClassification } from 'src/components/Profiles/components/ProfileDetails/ProfileSiteOfCareDetails';
import { handleNumericValue } from 'src/utils/handleNumericValue/handleNumericValue';
import { ResultsItemLists } from './components/ResultsItemLists';
import type { ProviderResultItemProps } from './types';

/**
 * A provider row in a results list, shared by Discovery and the Strategy Center map
 * results so the two always look alike. It only lays out the row; callers own the
 * data and behavior (selection, profile navigation, hover-to-highlight on the map,
 * which stats to show) and pass them in.
 *
 * @example
 * <ProviderResultItem
 *   provider={provider}
 *   city="New York"
 *   state="NY"
 *   isSelected={isSelected}
 *   onToggleSelect={handleToggle}
 *   href={profileUrl}
 *   stats={[{ key: 'claims', label: 'Claims', value: provider.claims }]}
 *   lists={provider.provider_lists}
 * />
 */
const ProviderResultItem = ({
  provider,
  city,
  state,
  isSelected,
  onToggleSelect,
  href,
  onNameClick,
  stats,
  lists,
  crmEntityType,
  actions,
  children,
  isActive,
  ref,
  onMouseEnter,
  onMouseLeave,
  'data-testid': testId,
}: ProviderResultItemProps) => {
  const nameContent = (
    <div className="flex-1-0-0 text-sky-900 text-base font-bold hover:underline hover:text-sky-800 ">
      <TypeIcon
        type={provider.soc_type || provider.content_type || provider.type}
        size="sm"
        className="text-sky-900 text-base pr-1"
        data-testid="mock-fa-icon"
      />
      {provider?.name}
    </div>
  );

  return (
    <div
      ref={ref}
      className={twMerge(
        'w-full px-4 py-3 flex flex-col gap-4 border-b border-neutral-100 last:border-b-0 cursor-pointer',
        isActive && 'bg-sky-100'
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={testId}
    >
      <div className="w-full flex gap-2">
        {/* Checkbox Column */}
        <div className="flex-none flex items-center h-6">
          <Checkbox
            size="sm"
            checked={isSelected}
            onChange={onToggleSelect}
            aria-label={`Select ${provider?.name}`}
            data-testid="mock-checkbox"
          />
        </div>

        {/* Main Content Column */}
        <div className="flex-1 min-w-0 flex flex-col gap-1 @container">
          {/* Top Row: Name and Stats */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 flex flex-col gap-1">
              {/* Name and Tags Row */}
              <div className="flex items-center gap-1">
                {href ? (
                  <Link
                    href={href}
                    onClick={onNameClick}
                    scroll={false}
                    data-testid="mock-link"
                  >
                    {nameContent}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="text-left cursor-pointer"
                    onClick={onNameClick}
                    aria-label={`View profile for ${provider?.name}`}
                    data-testid={`provider-name-${provider?.provider_id}`}
                  >
                    {nameContent}
                  </button>
                )}
                {provider?.is_retired && (
                  <div
                    className="px-2 py-1 bg-red-100 rounded-full border border-red-700 inline-flex justify-center items-center gap-1"
                    data-testid="mock-chip"
                  >
                    <p className="text-red-700 text-xs font-medium">Retired </p>
                  </div>
                )}
              </div>

              {/* Specialty and Location */}
              <div className="flex flex-col gap-1 text-primary text-sm font-normal font-['Inter'] leading-tight">
                <span>
                  {provider?.specialty ||
                    formatSocClassification({
                      soc_type: provider?.soc_type,
                      soc_subtype: provider?.soc_subtype,
                    })}
                </span>
                <span>{[city, state].filter(Boolean).join(', ')}</span>
              </div>
            </div>

            {/* Stats */}
            {stats?.length > 0 && (
              <div className="flex-none flex items-center gap-2">
                {stats.map((stat, index) => (
                  <React.Fragment key={stat.key}>
                    {index > 0 && <div className="w-px h-8 bg-neutral-200" />}
                    <div className="flex flex-col items-center gap-1">
                      <div
                        className="text-secondary text-sm flex items-center gap-1"
                        data-testid={stat.labelTestId}
                      >
                        {stat.label}
                      </div>
                      <div
                        className={twMerge(
                          'text-primary text-md',
                          stat.isActive ? 'font-bold' : 'font-normal'
                        )}
                      >
                        {stat.displayValue ?? handleNumericValue(stat.value)}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Bottom Row: Lists, tags, and crm button */}
          <div className="flex flex-col gap-2 pt-2 @sm:flex-row @sm:items-baseline @sm:gap-4">
            <div className="flex-1 min-w-0 flex-col items-start gap-1 inline-flex">
              {lists?.length > 0 && (
                <div className="inline-flex flex-shrink-0 w-full">
                  <ResultsItemLists lists={lists} />
                </div>
              )}
              {provider.tags?.length > 0 && (
                <div className="inline-flex flex-shrink-0">
                  <Tags tags={provider.tags} className="!w-auto" />
                </div>
              )}
              {children}
            </div>
            <div className="flex-shrink-0 self-end flex items-end">
              {actions && <div className="pr-1">{actions}</div>}
              <PushToCRMGroup provider={provider} entityType={crmEntityType} />
              <MedRMStatus provider={provider} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderResultItem;
